'use client';

import { clsx } from 'clsx';
import { usePathname } from 'next/navigation';
import { FC, ReactNode, useEffect, useMemo, useRef } from 'react';
import { Link } from 'src/general/components/Link/Link';
import styles from './ContentHubHero.module.scss';

interface Props {
  href: string;
  title: ReactNode;
}

export const ContentHubHeroNavLink: FC<Props> = ({ href, title }) => {
  const pathname = usePathname();
  const ref = useRef<HTMLLIElement>(null);
  const isActive = useMemo(() => {
    // Custom case for the "All articles" link
    if (href === '/street-smarts') {
      // If the pathname is exactly "/street-smarts" or "/street-smarts/[page_number]"
      return pathname === '/street-smarts' || pathname?.match(/^\/street-smarts\/\d+$/);
    }

    return !!pathname?.startsWith(href);
  }, [pathname]);

  // Horizontally scroll to the element so it's in the view
  useEffect(() => {
    if (isActive && ref.current && typeof ref.current.parentElement?.scroll !== 'undefined') {
      const windowWidth = window.innerWidth;
      const elementOffsetLeft = ref.current.offsetLeft;
      const elementOffsetRight = elementOffsetLeft + ref.current.clientWidth;
      const parentScroll = ref.current.parentElement.scrollLeft;
      if (elementOffsetRight - parentScroll > windowWidth || elementOffsetLeft - parentScroll < 0) {
        ref.current.parentElement?.scroll({ left: ref.current.offsetLeft });
      }
    }
  }, [isActive]);

  return (
    <li className={styles.navItem} ref={ref}>
      <Link href={href} className={clsx(styles.navLink, { [styles.active]: isActive })}>
        {title}
      </Link>
    </li>
  );
};
